import React, { useMemo, useState, useCallback, useRef } from "react";
import "./BranchDetailLedger.css";

const REMARK_LIMIT = 18;

const Remark = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= REMARK_LIMIT) return <span>{text}</span>;
  return (
    <span>
      {expanded ? text : `${text.slice(0, REMARK_LIMIT)}…`}
      <button className="remark-toggle-btn" onClick={() => setExpanded(!expanded)}>
        {expanded ? " less" : " more"}
      </button>
    </span>
  );
};

// Standalone editable cell — text/number input
const EditableCell = ({ tx, field, display, type = "text", className = "", style = {}, editingCell, editValue, onCellClick, onValueChange, onSave, onCancel }) => {
  const isActive = editingCell?.paymentId === tx?.paymentId && editingCell?.field === field;
  if (!tx) return <td className={className} style={style}></td>;

  if (isActive) {
    return (
      <td className={`${className} cell-editing`} style={style}>
        <input
          className="cell-input"
          type={type}
          value={editValue}
          autoFocus
          onChange={(e) => onValueChange(e.target.value)}
          onBlur={onSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onSave(); }
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
    );
  }

  return (
    <td className={`${className} cell-editable`} style={style} onClick={() => onCellClick(tx, field)} title="Click to edit">
      {display}
    </td>
  );
};

// Inline new entry row — shown in empty rows when clicked
const buildTimestamp = (summaryStart) => {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const nowUTC = Date.now();
  // Current time-of-day in IST (ms since midnight IST)
  const istNow = new Date(nowUTC + IST_OFFSET_MS);
  const timeOfDayMs = istNow.getUTCHours() * 3600000 + istNow.getUTCMinutes() * 60000 + istNow.getUTCSeconds() * 1000 + istNow.getUTCMilliseconds();
  // summaryStart is an IST date string — midnight IST = (summaryStart 00:00 IST) in UTC
  const midnightISTasUTC = new Date(`${summaryStart}T00:00:00.000Z`).getTime() - IST_OFFSET_MS;
  return new Date(midnightISTasUTC + timeOfDayMs).toISOString();
};

const NewEntryRow = ({ rowIndex, branchId, allBranches, apiUrl, partyApiUrl, onSave, onDismiss, summaryStart }) => {
  const [mode, setMode] = useState("branch"); // "branch" | "tp"
  const [type, setType] = useState("IN");
  const [toBranch, setToBranch] = useState("");
  const [thirdParty, setThirdParty] = useState("");
  const [createLakEntry, setCreateLakEntry] = useState(true);
  const [amount, setAmount] = useState("");
  const [commission, setCommission] = useState("");
  const [remarks, setRemarks] = useState("");
  const [posting, setPosting] = useState(false);
  const [parties, setParties] = useState([]);

  // Fetch party list when switching to TP mode
  React.useEffect(() => {
    if (mode !== "tp" || !partyApiUrl || parties.length > 0) return;
    fetch(partyApiUrl)
      .then((r) => r.json())
      .then((d) => setParties(d.branches || []))
      .catch(() => {});
  }, [mode, partyApiUrl, parties.length]);

  const isSelf = toBranch === "" || toBranch === branchId;
  const isEXP = type === "EXP";
  const isTP = mode === "tp";
  const pType = type === "IN" ? "OUT" : type === "OUT" ? "IN" : type === "BTIN" ? "BTOUT" : type === "BTOUT" ? "BTIN" : null;

  const handlePost = async () => {
    if (!amount || isNaN(Number(amount))) return alert("Enter a valid amount");
    if (isTP && !thirdParty) return alert("Select a 3rd party");
    setPosting(true);
    try {
      const baseId = `ADM-${Date.now()}`;
      const amt = parseFloat(amount);
      const comm = parseFloat(commission) || 0;
      const timestamp = buildTimestamp(summaryStart);

      const payload = [];

      if (isTP) {
        const lakOppositeType = pType;
        // Main 3rd party transaction for this branch
        payload.push({
          paymentId: `${baseId}-TP`,
          paymentType: type,
          flowRole: "SENDER",
          fromBranch: branchId,
          toBranch: thirdParty,
          amount: amt,
          commission: comm,
          status: "DELIVERED",
          remarks: `[ADMIN POST] ${remarks}`,
          timestamp,
          linkedId: createLakEntry ? `${baseId}-LAK` : null,
          thirdParty,
        });

        // LAK counter entry (mirrors BranchHome pattern exactly)
        if (createLakEntry) {
          payload.push({
            paymentId: `${baseId}-LAK`,
            paymentType: lakOppositeType,
            flowRole: "SENDER",
            fromBranch: "LAK",
            toBranch: branchId,
            amount: amt,
            commission: 0,
            status: "DELIVERED",
            remarks: `[LAK-TP: ${thirdParty}] ${remarks}`,
            timestamp,
            linkedId: `${baseId}-TP`,
            thirdParty,
          });
        }
      } else {
        // Existing branch-to-branch / self / EXP logic
        const sender = {
          paymentId: `${baseId}-${type}`,
          paymentType: type,
          flowRole: "SENDER",
          fromBranch: branchId,
          toBranch: isSelf || isEXP ? branchId : toBranch,
          amount: amt,
          commission: comm,
          status: isSelf || isEXP ? "DELIVERED" : "PENDING",
          remarks: `[ADMIN POST] ${remarks}`,
          timestamp,
          linkedId: (!isSelf && !isEXP && pType) ? `${baseId}-${pType}` : null,
          senderName: null, senderCell: null, receiverName: null, receiverCell: null,
          thirdParty: null,
        };
        payload.push(sender);

        if (!isSelf && !isEXP && pType) {
          payload.push({
            ...sender,
            paymentId: `${baseId}-${pType}`,
            paymentType: pType,
            flowRole: "RECEIVER",
            commission: 0,
            linkedId: sender.paymentId,
          });
        }
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactions: payload }),
      });
      const resBody = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resBody.error || resBody.message || "Post failed");
      onDismiss();
      onSave();
    } catch (err) {
      alert("Post failed: " + err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <tr className="new-entry-row">
      <td className="col-sno" style={{ color: "#94a3b8" }}>{rowIndex + 1}</td>
      <td colSpan={9}>
        <div className="new-entry-form" style={{ flexWrap: "wrap", gap: "6px" }}>
          {/* Mode toggle */}
          <div className="ne-mode-toggle">
            <button
              type="button"
              className={`ne-mode-btn ${mode === "branch" ? "active" : ""}`}
              onClick={() => setMode("branch")}
            >Branch</button>
            <button
              type="button"
              className={`ne-mode-btn ${mode === "tp" ? "active" : ""}`}
              onClick={() => setMode("tp")}
            >3rd Party</button>
          </div>

          <select className="ne-input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
            <option value="BTIN">BT IN</option>
            <option value="BTOUT">BT OUT</option>
            {!isTP && <option value="EXP">EXP</option>}
          </select>

          {isTP ? (
            <select className="ne-input" value={thirdParty} onChange={(e) => setThirdParty(e.target.value)} required>
              <option value="">— 3rd Party —</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>{p.partyName}</option>
              ))}
            </select>
          ) : (
            !isEXP && (
              <select className="ne-input" value={toBranch} onChange={(e) => setToBranch(e.target.value)}>
                <option value="">— Self —</option>
                {allBranches.filter((b) => b !== branchId).map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            )
          )}

          <input className="ne-input" type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <input className="ne-input" type="number" placeholder="Comm" value={commission} onChange={(e) => setCommission(e.target.value)} />
          <input
            className="ne-input ne-remarks"
            placeholder="Remarks…"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handlePost(); if (e.key === "Escape") onDismiss(); }}
          />

          {isTP && (
            <label className="ne-lak-toggle" title="Also create a counter entry for LAK">
              <input
                type="checkbox"
                checked={createLakEntry}
                onChange={(e) => setCreateLakEntry(e.target.checked)}
              />
              LAK entry
            </label>
          )}

          <button className="ne-post-btn" onClick={handlePost} disabled={posting}>
            {posting ? "…" : "✓"}
          </button>
          <button className="ne-cancel-btn" onClick={onDismiss}>✕</button>
        </div>
      </td>
    </tr>
  );
};

// Standalone select cell — dropdown for branch name
const SelectCell = ({ tx, field, display, options, className = "", style = {}, editingCell, editValue, onCellClick, onValueChange, onSave, onCancel }) => {
  const isActive = editingCell?.paymentId === tx?.paymentId && editingCell?.field === field;
  if (!tx) return <td className={className} style={style}></td>;

  if (isActive) {
    return (
      <td className={`${className} cell-editing`} style={style}>
        <select
          className="cell-input"
          value={editValue}
          autoFocus
          onChange={(e) => onValueChange(e.target.value)}
          onBlur={onSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onSave(); }
            if (e.key === "Escape") onCancel();
          }}
        >
          {options.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </td>
    );
  }

  return (
    <td className={`${className} cell-editable`} style={style} onClick={() => onCellClick(tx, field)} title="Click to change branch">
      {display}
    </td>
  );
};

const BranchDetailLedger = ({
  branchId,
  transactions,
  closingBalInfo,
  summaryStart,
  summaryEnd,
  opBalance,
  totalIn,
  totalOut,
  btIn,
  btOut,
  exp,
  comm,
  apiUrl,
  partyApiUrl,
  onSave,
  onClose,
  allBranches = [],
}) => {
  const [editingCell, setEditingCell] = useState(null); // { paymentId, field }
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [localTransactions, setLocalTransactions] = useState(transactions);
  const [newEntryRow, setNewEntryRow] = useState(null); // index of bottom new-entry row
  const [topEntryActive, setTopEntryActive] = useState(false); // header "+ New Entry" row
  const isSavingRef = useRef(false); // prevents double-fire from Enter + onBlur

  // Sync local copy when parent refreshes
  React.useEffect(() => { setLocalTransactions(transactions); }, [transactions]);

  const data = useMemo(() => {
    const branchTx = localTransactions.filter((tx) => {
      const isMyRole =
        (tx.fromBranch === branchId && tx.flowRole === "SENDER") ||
        (tx.toBranch === branchId && tx.flowRole === "RECEIVER") ||
        tx.thirdParty === branchId;

      const isBaseValid = tx.status !== "REJECTED" && tx.status !== "SPLIT";
      const isIncomingReady = !(
        tx.flowRole === "RECEIVER" &&
        (tx.status === "PENDING" || tx.status === "WAITING")
      );

      const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
      const txDate = tx.timestamp
        ? new Date(new Date(tx.timestamp).getTime() + IST_OFFSET_MS)
            .toISOString()
            .split("T")[0]
        : null;
      const isWithinDate = txDate >= summaryStart && txDate <= summaryEnd;

      return isMyRole && isBaseValid && isIncomingReady && isWithinDate;
    });

    const result = {
      inflow: [],
      outflow: [],
      btIn: [],
      btOut: [],
      expenses: [],
      totals: { in: 0, out: 0, btIn: 0, btOut: 0, exp: 0, comm: 0 },
    };

    branchTx.forEach((tx) => {
      const amount = Number(tx.amount || 0);
      const commission = Number(tx.commission || 0);
      const isThisLedgerThirdParty = tx.thirdParty === branchId;

      if (["BTIN", "BTSPLITIN"].includes(tx.paymentType)) {
        result.btIn.push(tx);
        if (!isThisLedgerThirdParty) result.totals.btIn += amount;
      } else if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType)) {
        result.btOut.push(tx);
        if (!isThisLedgerThirdParty) result.totals.btOut += amount;
      } else if (tx.paymentType === "EXP") {
        result.expenses.push(tx);
        result.totals.exp += amount;
      } else if (["IN", "SPLITIN"].includes(tx.paymentType)) {
        result.inflow.push(tx);
        if (!isThisLedgerThirdParty) result.totals.in += amount;
      } else if (["OUT", "SPLITOUT"].includes(tx.paymentType)) {
        result.outflow.push(tx);
        if (!isThisLedgerThirdParty) result.totals.out += amount;
      }

      // Count branch commission: always include tx.commission (branch's own cut)
      // tpCommission (admin-set) is excluded — it only affects the TP ledger
      if (commission > 0) {
        result.totals.comm += commission;
      }
    });

    return result;
  }, [localTransactions, branchId, summaryStart, summaryEnd]);

  const handleCellClick = useCallback((tx, field) => {
    if (!apiUrl || !onSave) return;
    setEditingCell({ paymentId: tx.paymentId, field });
    setEditValue(String(tx[field] ?? ""));
  }, [apiUrl, onSave]);

  const handleSave = useCallback(async (tx) => {
    if (!editingCell || isSavingRef.current) return;
    isSavingRef.current = true;

    const field = editingCell.field;
    const originalValue = String(tx[field] ?? "");

    setEditingCell(null); // close input immediately

    if (editValue === originalValue) {
      isSavingRef.current = false;
      return;
    }

    setSaving(true);
    try {
      const payload = {
        paymentId: tx.paymentId,
        paymentType: tx.paymentType,
        amount: field === "amount" ? Number(editValue) : Number(tx.amount),
        commission: field === "commission" ? Number(editValue) : Number(tx.commission || 0),
        remarks: field === "remarks" ? editValue : tx.remarks,
        senderName: tx.senderName || null,
        senderCell: tx.senderCell || null,
        receiverName: tx.receiverName || null,
        receiverCell: tx.receiverCell || null,
        status: tx.status,
        ...(field === "fromBranch" && { fromBranch: editValue }),
        ...(field === "toBranch" && { toBranch: editValue }),
        editingBranch: branchId,
        linkedId: tx.linkedId || null,
      };

      console.log("[BranchDetailLedger] PATCH payload:", payload);
      const res = await fetch(apiUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const resBody = await res.json().catch(() => ({}));
      console.log("[BranchDetailLedger] PATCH response:", res.status, resBody);

      if (!res.ok) throw new Error(resBody.error || "Save failed");

      // Find linked transaction — by linkedId if present, else by shared base ID (TX-timestamp prefix)
      const baseId = tx.paymentId.match(/^([A-Z]+-\d+)/)?.[1];
      const linkedTx = localTransactions.find((t) =>
        t.paymentId !== tx.paymentId && (
          (tx.linkedId && t.paymentId === tx.linkedId) ||
          (!tx.linkedId && baseId && t.paymentId.startsWith(baseId))
        )
      );
      console.log("[LinkedCheck] tx.paymentId:", tx.paymentId, "| baseId:", baseId, "| linkedId:", tx.linkedId, "| linkedTx found:", linkedTx?.paymentId ?? "NOT FOUND");

      if (linkedTx) {
        const linkedPayload = {
            paymentId: linkedTx.paymentId,
            paymentType: linkedTx.paymentType,
            amount: field === "amount" ? Number(editValue) : Number(linkedTx.amount),
            commission: Number(linkedTx.commission || 0),
            remarks: field === "remarks" ? editValue : linkedTx.remarks,
            senderName: linkedTx.senderName || null,
            senderCell: linkedTx.senderCell || null,
            receiverName: linkedTx.receiverName || null,
            receiverCell: linkedTx.receiverCell || null,
            status: linkedTx.status,
            fromBranch: field === "fromBranch" ? editValue : linkedTx.fromBranch,
            toBranch: field === "toBranch" ? editValue : linkedTx.toBranch,
            editingBranch: branchId,
            linkedId: linkedTx.linkedId || null,
          };
        console.log("[BranchDetailLedger] Patching linked transaction:", linkedPayload);
        const linkedRes = await fetch(apiUrl, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(linkedPayload),
        });
        const linkedResBody = await linkedRes.json().catch(() => ({}));
        console.log("[LinkedPATCH] response:", linkedRes.status, linkedResBody);
      }

      // Patch local copy for both primary and linked — instant UI update
      const numericFields = ["amount", "commission"];
      const patchedValue = numericFields.includes(field) ? Number(editValue) : editValue;
      setLocalTransactions((prev) =>
        prev.map((t) => {
          if (t.paymentId === tx.paymentId) return { ...t, [field]: patchedValue };
          if (t.paymentId === tx.linkedId) return { ...t, [field]: patchedValue };
          return t;
        })
      );

      onSave(); // refresh summary totals in background
    } catch (err) {
      console.error("[BranchDetailLedger] Save error:", err);
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  }, [editingCell, editValue, branchId, apiUrl, onSave, localTransactions]);

  const handleCancel = useCallback(() => setEditingCell(null), []);

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

  const getPartnerIn = (item) => {
    if (!item) return "";
    return item.toBranch === branchId ? item.fromBranch : item.toBranch;
  };

  const getPartnerOut = (item) => {
    if (!item) return "";
    return item.fromBranch === branchId ? item.toBranch : item.fromBranch;
  };

  const rowCount = Math.max(data.inflow.length, data.outflow.length, 10) + 1;
  const rows = Array.from({ length: rowCount });

  const cellProps = (tx, field, display, type, className, style) => ({
    tx, field, display, type, className, style,
    editingCell, editValue,
    onCellClick: handleCellClick,
    onValueChange: setEditValue,
    onSave: () => handleSave(tx),
    onCancel: handleCancel,
  });

  // For IN row: partner is fromBranch (sender side) or toBranch based on flow role
  const getInPartnerField = (item) =>
    item?.toBranch === branchId ? "fromBranch" : "toBranch";

  // For OUT row: partner is toBranch (sender side) or fromBranch based on flow role
  const getOutPartnerField = (item) =>
    item?.fromBranch === branchId ? "toBranch" : "fromBranch";

  const selectCellProps = (tx, field, display, className, style) => ({
    tx, field, display, className, style,
    options: allBranches.filter((b) => b !== branchId),
    editingCell, editValue,
    onCellClick: handleCellClick,
    onValueChange: setEditValue,
    onSave: () => handleSave(tx),
    onCancel: handleCancel,
  });

  return (
    <div className="ledger-container">

      {saving && <div className="ledger-saving-bar">Saving…</div>}

      <div className="ledger-header-banner">
        <h2>Branch Ledger — {branchId}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span className="ledger-date-badge">
            {summaryStart === summaryEnd ? summaryStart : `${summaryStart} → ${summaryEnd}`}
          </span>
          {onClose && (
            <button className="ledger-close-btn" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>

      <div className="ledger-grid">

        {/* LEFT: Cash IN / OUT */}
        <div className="ledger-section-card">
          <div className="ledger-section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Cash Transactions</span>
            {apiUrl && onSave && !topEntryActive && newEntryRow === null && (
              <button
                className="ne-add-header-btn"
                onClick={() => setTopEntryActive(true)}
                title="Add new entry"
              >
                + New Entry
              </button>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="excel-table">
              <thead>
                <tr>
                  <th className="col-sno">#</th>
                  <th>Name (IN)</th>
                  <th>IN ₹</th>
                  <th>Com</th>
                  <th>Remarks</th>
                  <th style={{ background: "#e2e8f0", width: 4 }}></th>
                  <th>Name (OUT)</th>
                  <th>OUT ₹</th>
                  <th>Com</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {topEntryActive && (
                  <NewEntryRow
                    key="top-entry"
                    rowIndex={0}
                    branchId={branchId}
                    allBranches={allBranches}
                    apiUrl={apiUrl}
                    partyApiUrl={partyApiUrl}
                    onSave={onSave}
                    onDismiss={() => setTopEntryActive(false)}
                    summaryStart={summaryStart}
                  />
                )}
                {rows.map((_, i) => {
                  const inItem = data.inflow[i];
                  const outItem = data.outflow[i];
                  const isEmpty = !inItem && !outItem;

                  // First empty row after data — show new entry option
                  const dataEndIndex = Math.max(data.inflow.length, data.outflow.length);
                  if (isEmpty && apiUrl && onSave && i === dataEndIndex) {
                    if (newEntryRow === i) {
                      return (
                        <NewEntryRow
                          key={i}
                          rowIndex={i}
                          branchId={branchId}
                          allBranches={allBranches}
                          apiUrl={apiUrl}
                          partyApiUrl={partyApiUrl}
                          onSave={onSave}
                          onDismiss={() => setNewEntryRow(null)}
                          summaryStart={summaryStart}
                        />
                      );
                    }
                    return (
                      <tr key={i} className="empty-row" onClick={() => setNewEntryRow(i)} title="Click to add new entry">
                        <td className="col-sno" style={{ color: "#94a3b8" }}>{i + 1}</td>
                        <td colSpan={9} className="empty-row-hint">+ new entry</td>
                      </tr>
                    );
                  }

                  // Remaining empty rows — plain blank
                  if (isEmpty) {
                    return (
                      <tr key={i}>
                        <td className="col-sno" style={{ color: "#94a3b8" }}>{i + 1}</td>
                        <td colSpan={9}></td>
                      </tr>
                    );
                  }

                  const isInTP = inItem?.thirdParty && inItem?.thirdParty !== "NONE" && inItem?.thirdParty !== branchId;
                  const isOutTP = outItem?.thirdParty && outItem?.thirdParty !== "NONE" && outItem?.thirdParty !== branchId;
                  return (
                    <tr key={i}>
                      <td className="col-sno">{i + 1}</td>
                      <SelectCell {...selectCellProps(inItem, getInPartnerField(inItem), getPartnerIn(inItem), "col-in-name", {})} />
                      <EditableCell {...cellProps(inItem, "amount", inItem ? fmt(inItem.amount) : "", "number", "col-in-amt", { opacity: isInTP ? 0.35 : 1 })} />
                      <EditableCell {...cellProps(inItem, "commission", inItem?.commission || "", "number", "col-in-comm", {})} />
                      <EditableCell {...cellProps(inItem, "remarks", inItem ? <Remark text={inItem.remarks} /> : "", "text", "col-remarks", {})} />
                      <td className="col-divider"></td>
                      <SelectCell {...selectCellProps(outItem, getOutPartnerField(outItem), getPartnerOut(outItem), "col-out-name", {})} />
                      <EditableCell {...cellProps(outItem, "amount", outItem ? fmt(outItem.amount) : "", "number", "col-out-amt", { opacity: isOutTP ? 0.35 : 1 })} />
                      <EditableCell {...cellProps(outItem, "commission", outItem?.commission || "", "number", "col-out-comm", {})} />
                      <EditableCell {...cellProps(outItem, "remarks", outItem ? <Remark text={outItem.remarks} /> : "", "text", "col-remarks", {})} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* MIDDLE: Summary */}
        <div className="ledger-section-card">
          <div className="ledger-section-title">Daily Summary</div>
          <table className="summary-table">
            <tbody>
              <tr className="summary-opening-row">
                <td className="s-label">Opening Balance</td>
                <td className="s-val blue">₹{fmt(opBalance)}</td>
              </tr>
              <tr>
                <td className="s-label">Cash IN</td>
                <td className="s-val green">₹{fmt(totalIn)}</td>
              </tr>
              <tr>
                <td className="s-label">BT IN (+)</td>
                <td className="s-val green">₹{fmt(btIn)}</td>
              </tr>
              <tr>
                <td className="s-label">Cash OUT</td>
                <td className="s-val red">₹{fmt(totalOut)}</td>
              </tr>
              <tr>
                <td className="s-label">BT OUT (−)</td>
                <td className="s-val red">₹{fmt(btOut)}</td>
              </tr>
              <tr>
                <td className="s-label">Commission</td>
                <td className="s-val orange">₹{fmt(comm)}</td>
              </tr>
              <tr className="summary-section-header">
                <td colSpan="2">Expenses</td>
              </tr>
              {data.expenses.length === 0 && (
                <tr><td colSpan="2" style={{ color: "#94a3b8", textAlign: "center", padding: "8px" }}>No expenses</td></tr>
              )}
              {data.expenses.map((ex, idx) => (
                <tr key={idx} className="exp-row">
                  <EditableCell {...cellProps(ex, "remarks", ex.remarks || "Expense", "text", "", {})} />
                  <EditableCell {...cellProps(ex, "amount", `₹${fmt(ex.amount)}`, "number", "", {})} />
                </tr>
              ))}
              <tr>
                <td className="s-label">Total Expenses</td>
                <td className="s-val red">₹{fmt(exp)}</td>
              </tr>
              <tr className="summary-closing-row">
                <td className="s-label">Closing Balance</td>
                <td className="s-val">₹{fmt(closingBalInfo)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* RIGHT: BT Details */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="ledger-section-card">
            <div className="ledger-section-title">BT IN Details</div>
            <table className="excel-table bt-table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {data.btIn.length === 0 && (
                  <tr><td colSpan="3" style={{ color: "#94a3b8", textAlign: "center", padding: "8px" }}>No BT IN</td></tr>
                )}
                {data.btIn.map((tx, i) => (
                  <tr key={i}>
                    <SelectCell {...selectCellProps(tx, tx.fromBranch === branchId ? "toBranch" : "fromBranch", `${tx.fromBranch} → ${tx.toBranch}`, "", {})} />
                    <EditableCell {...cellProps(tx, "amount", `₹${fmt(tx.amount)}`, "number", "", { textAlign: "right", color: "#16a34a", fontWeight: 700 })} />
                    <EditableCell {...cellProps(tx, "remarks", <Remark text={tx.remarks} />, "text", "", {})} />
                  </tr>
                ))}
                <tr className="bt-total-row">
                  <td>Total BT IN</td>
                  <td>₹{fmt(data.totals.btIn)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="ledger-section-card">
            <div className="ledger-section-title">BT OUT Details</div>
            <table className="excel-table bt-table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {data.btOut.length === 0 && (
                  <tr><td colSpan="3" style={{ color: "#94a3b8", textAlign: "center", padding: "8px" }}>No BT OUT</td></tr>
                )}
                {data.btOut.map((tx, i) => (
                  <tr key={i}>
                    <SelectCell {...selectCellProps(tx, tx.fromBranch === branchId ? "toBranch" : "fromBranch", `${tx.fromBranch} → ${tx.toBranch}`, "", {})} />
                    <EditableCell {...cellProps(tx, "amount", `₹${fmt(tx.amount)}`, "number", "", { textAlign: "right", color: "#dc2626", fontWeight: 700 })} />
                    <EditableCell {...cellProps(tx, "remarks", <Remark text={tx.remarks} />, "text", "", {})} />
                  </tr>
                ))}
                <tr className="bt-total-row">
                  <td>Total BT OUT</td>
                  <td>₹{fmt(data.totals.btOut)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BranchDetailLedger;
